import React, { useCallback, useEffect } from "react"
import { styled, ThemeProvider, DarkTheme } from "baseui"
import { Theme } from "baseui/theme"
import { Button, KIND } from "baseui/button"
import Logo from "~/components/Icons/Logo"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import Play from "~/components/Icons/Play"
import { Block } from "baseui/block"
import { useEditor } from "@layerhub-io/react"
import useEditorType from "~/hooks/useEditorType"
import { IScene } from "@layerhub-io/types"
import { loadTemplateFonts } from "~/utils/fonts"
import { loadVideoEditorAssets } from "~/utils/video"
import DesignTitle from "./DesignTitle"
import { IDesign } from "~/interfaces/DesignEditor"
import useAppContext from "~/hooks/useAppContext"
import Loading from "~/components/Loading"
import Extras from "../Extras/Extras"
import ErrorModal from "../ErrorModal/ErrorModal"
import ExportProgressModal from "../ExportProgressModal/ExportProgressModal"
import Cookies from 'js-cookie';
import { exportAllAsZip, detectProductSize, ExportProgress } from '~/utils/pdfExport';
import { clearEnvCookie, getAppEnv } from "~/utils/env";

const Container = styled<"div", {}, Theme>("div", ({ $theme }) => ({
  height: "64px",
  background: $theme.colors.black,
  display: "grid",
  padding: "0 1.25rem",
  gridTemplateColumns: "280px 1fr 480px",
  alignItems: "center",
}))

const getIncludedSpreadCount = (skuData: any) => {
  const standardPageSize = Number(skuData?.standard_page_size || 0)
  const totalSpreadsPerBook = Number(skuData?.total_spreads_per_book || 0)
  return standardPageSize > 0 ? standardPageSize : totalSpreadsPerBook
}

const hasCoverScene = () => {
  const sku = (Cookies.get('photobookSku') || "").toUpperCase()
  const coverFinish = (Cookies.get('cover_finish') || "").toLowerCase()
  return sku.includes("PH") || coverFinish.includes("personalized") || coverFinish.includes("personalised")
}

export default function () {
  const { setDisplayPreview, setScenes, setCurrentDesign, currentDesign, scenes, showDottedLine, setShowDottedLine } = useDesignEditorContext()
  const editorType = useEditorType()
  const editor = useEditor()
  const { skuData } = useAppContext()
  const inputFileRef = React.useRef<HTMLInputElement>(null)
  const [loading, setLoading] = React.useState(false)
  const [exportProgress, setExportProgress] = React.useState<ExportProgress | null>(null)
  const [total, setTotal] = React.useState(0)
  const [finalizing, setFinalizing] = React.useState(false)
  let graphic = {}
  const isDownload = Cookies.get('isDownload') === 'true'
  const isPremium =
    window.location.pathname.includes("/premium-") ||
    Cookies.get('premiumMode') === 'true' ||
    Boolean(Cookies.get('premiumOrderItemId'))
  const sku = Cookies.get('photobookSku') || ""

  const { extrasModalOpen, setExtrasModalOpen, errorModalOpen, setErrorModalOpen } = useDesignEditorContext()

  const isLoggedIn = localStorage.getItem("customerId") === null ? false : true;

  useEffect(() => {
    getPriceTotal()
  }, [scenes.length])

  const { apiService } = useAppContext()

  const parseGraphicJSON = () => {
    if (editor) {
      const currentScene = editor.scene.exportToJSON()

      const updatedScenes = scenes.map((scn) => {
        if (scn.id === currentScene.id) {
          return {
            id: currentScene.id,
            layers: currentScene.layers,
            name: currentScene.name,
            // Preserve per-scene frame so layflat cover dimensions survive save/reload
            frame: (currentScene as any).frame || (scn as any).frame,
          }
        }
        return {
          id: scn.id,
          layers: scn.layers,
          name: scn.name,
          // Preserve per-scene frame (layflat cover has a different frame than inner pages)
          frame: (scn as any).frame,
        }
      })

      if (currentDesign) {
        const graphicTemplate: IDesign = {
          id: currentDesign.id,
          type: "GRAPHIC",
          name: currentDesign.name,
          frame: currentDesign.frame,
          scenes: updatedScenes,
          metadata: {},
          preview: "",
        }

        graphic = graphicTemplate
        return graphicTemplate
      } else {
        console.log("NO CURRENT DESIGN")
      }
    }
  }

  const openModal = () => {
    if (isPremium) {
      return
    }

    const graphic = parseGraphicJSON();
    const coverOffset = hasCoverScene() ? 1 : 0
    const scenesTotal = graphic.scenes.length - coverOffset;
    const multiples = Number(skuData?.extra_pages_multiple || 1);
    const includedSpreadCount = getIncludedSpreadCount(skuData)

    const extraPages = scenesTotal - includedSpreadCount;
    if (extraPages > 0 && extraPages % multiples !== 0 && multiples > 1) {
      setErrorModalOpen(true)
      return
    }

    //setExtrasModalOpen(true)
    addToCart()
  }

  const exportScenesToZip = async () => {
    setLoading(true);
    setExportProgress({ stage: 'Starting…', current: 0, total: 1 });

    try {
      const currentScene = editor.scene.exportToJSON();
      const updatedScenes = scenes.map((scn) =>
        scn.id === currentScene.id ? currentScene : scn
      );

      const frame = currentDesign?.frame || scenes[0]?.frame || currentScene.frame;
      const width = frame?.width || 3570;
      const height = frame?.height || 2546;
      const productSize = detectProductSize(width, height);

      const timestamp = new Date().toISOString().slice(0, 10);

      await exportAllAsZip(
        updatedScenes,
        editor.renderer,
        productSize,
        sku,
        (progress) => setExportProgress(progress),
        `photobook-${productSize}-${timestamp}`
      );
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
      setExportProgress(null);
    }
  };

  const finalizePremiumProject = async () => {
    const orderItemId = Cookies.get('premiumOrderItemId')
    if (!isPremium || !orderItemId) {
      return
    }

    const shouldFinalize = window.confirm(
      "Finalize this premium order and mark it ready for production?"
    )
    if (!shouldFinalize) {
      return
    }

    try {
      setFinalizing(true)
      const graphic = parseGraphicJSON()
      await apiService.finalizePremiumOrder(orderItemId, graphic)

      clearEnvCookie("activeDesignId")
      clearEnvCookie("photobookSku")
      clearEnvCookie("premiumMode")
      clearEnvCookie("premiumOrderItemId")
      clearEnvCookie("premiumDesignId")
      localStorage.removeItem("extras")

      if (window.opener) {
        window.opener.location.reload()
      }

      window.close()

      if (!window.closed) {
        window.location.href = document.referrer || getAppEnv().shopBaseUrl
      }
    } catch (error) {
      console.error("Error finalizing premium order:", error)
      alert("Could not finalize the premium order. Please try again.")
    } finally {
      setFinalizing(false)
    }
  }

  const exit = (isFromAddingCart = false) => {
    // Clear environment-scoped cookies.
    clearEnvCookie("activeDesignId");
    clearEnvCookie("photobookSku");
    clearEnvCookie("premiumMode");
    clearEnvCookie("premiumOrderItemId");
    clearEnvCookie("premiumDesignId");
    localStorage.removeItem('savedProject');
    localStorage.removeItem('currentSessionDesign');
    localStorage.removeItem('premiumUploads');
    localStorage.removeItem('activeDesignId');
    localStorage.removeItem('extras');

    let url = getAppEnv().shopBaseUrl;
    if (isFromAddingCart) {
      url += '/cart';
    }
    window.location.href = url;

  }


  const makeDownload = (data: Object) => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data))
    const a = document.createElement("a")
    a.href = dataStr
    a.download = "template.json"
    a.click()
  }

  const makeDownloadTemplate = async () => {
    if (editor) {
      const graphic = parseGraphicJSON()
      /* const currentScene = editor.scene.exportToJSON()

      //map layers print left and top fields only as json
      const layers = currentScene.layers.map((layer) => {
        return {
          left: layer.left,
          top: layer.top,
        }
      }) */
      //console.log("currentScene", currentScene)
      //console.log(layers)
      //return
      makeDownload(graphic)
    }
  }



  const loadPresentationTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (const scn of scns) {
      const scene: IScene = {
        name: scn.name,
        frame: payload.frame,
        id: scn,
        layers: scn.layers,
        metadata: {},
      }
      const loadedScene = await loadVideoEditorAssets(scene)

      const preview = (await editor.renderer.render(loadedScene)) as string
      await loadTemplateFonts(loadedScene)
      scenes.push({ ...loadedScene, preview })
    }
    return { scenes, design }
  }


  const handleImportTemplate = React.useCallback(
    async (data: any) => {
      let template
      template = await loadGraphicTemplate(data)
      //   @ts-ignore
      setScenes(template.scenes)
      //   @ts-ignore
      setCurrentDesign(template.design)
    },
    [editor]
  )

  const loadGraphicTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (const scn of scns) {
      const scene: IScene = {
        name: scn.name,
        frame: payload.frame,
        id: scn.id,
        layers: scn.layers,
        metadata: {},
      }
      const loadedScene = await loadVideoEditorAssets(scene)
      await loadTemplateFonts(loadedScene)

      const preview = (await editor.renderer.render(loadedScene)) as string
      scenes.push({ ...loadedScene, preview })
    }

    return { scenes, design }
  }

  const handleInputFileRefClick = () => {
    inputFileRef.current?.click()
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (res) => {
        const result = res.target!.result as string
        const design = JSON.parse(result)
        handleImportTemplate(design)
      }
      reader.onerror = (err) => {
        console.log(err)
      }

      reader.readAsText(file)
    }
  }

  async function next() {
    setExtrasModalOpen(false)
    addToCart()
  }

  async function addToCart(): Promise<void> {
    setLoading(true);
    try {
      const graphic = parseGraphicJSON();

      let firstScene = graphic.scenes[0];
      firstScene.frame = graphic.frame;

      const preview = (await editor.renderer.render(firstScene)) as string;
      //graphic.preview = preview

      //count the number of scenes
      const scenesTotal = graphic.scenes.length;

      const { cartId, itemId } = await apiService.addToCart();

      const design = {
        design: graphic,
        extras: JSON.stringify(localStorage.getItem("extras"))
      }

      await apiService.updateCartItemDesign(cartId, itemId, design);

      await apiService.updateItemPrice(cartId, itemId, total);
      // add logic to update totals if the pages on the cart are more than standard

      exit(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Optionally, you can show an error message to the user here
    } finally {
      setLoading(false);
    }
  }

  function getPriceTotal(): number {
    if (editor) {
      const graphic = parseGraphicJSON();
      const coverOffset = hasCoverScene() ? 1 : 0
      const scenesTotal = graphic.scenes.length - coverOffset;
      const includedSpreadCount = getIncludedSpreadCount(skuData)
      if (scenesTotal > includedSpreadCount) {
        const additionalPages = scenesTotal - includedSpreadCount;
        const pageMultiple = Number(skuData?.extra_pages_multiple || 1)
        const pricePerPage = Number(skuData?.price_per_page || 0)
        const basePrice = Number(skuData?.price || 0)
        const additionalPagesPrice = (additionalPages / pageMultiple) * pricePerPage;
        const total = basePrice + additionalPagesPrice;
        setTotal(total)
        return parseFloat(total.toFixed(2));
      }

    }
    setTotal(parseFloat((skuData?.price || 0).toFixed(2)))
    return parseFloat((skuData?.price || 0).toFixed(2));
  }


  const autoSaveDesign = useCallback(async () => {
    if (currentDesign) {
      const graphic = parseGraphicJSON();

      if (graphic) {
        localStorage.setItem('currentSessionDesign', JSON.stringify(graphic));
        localStorage.removeItem('savedProject');
        if (isPremium && Cookies.get('premiumOrderItemId')) {
          await apiService.savePremiumOrderDesign(
            Cookies.get('premiumOrderItemId'),
            graphic,
            Cookies.get('premiumDesignId') || graphic.id
          );
        }
      } else {
        console.log("Auto-save failed: No graphic to save");
      }
    }
  }, [apiService, isPremium, parseGraphicJSON]);

  useEffect(() => {
    const interval = setInterval(autoSaveDesign, 10000)
    return () => clearInterval(interval)
  }, [autoSaveDesign])

  return (

    <>{
      <ErrorModal
        isOpen={!isPremium && errorModalOpen}
        setIsOpen={setErrorModalOpen}
        multiplier={skuData?.extra_pages_multiple}
        extraPages={(() => {
          const graphic = parseGraphicJSON();
          if (!graphic) return 0;
          const coverOffset = hasCoverScene() ? 1 : 0;
          const scenesTotal = graphic.scenes.length - coverOffset;
          const includedSpreadCount = getIncludedSpreadCount(skuData);
          return Math.max(0, scenesTotal - includedSpreadCount);
        })()} />}{
        <Extras
          isOpen={!isPremium && extrasModalOpen}
          setIsOpen={setExtrasModalOpen}
          total={total}
          next={next} />}
      <ExportProgressModal
        isOpen={loading && exportProgress !== null}
        progress={exportProgress}
      />
      {
        //@ts-ignore
        <ThemeProvider theme={DarkTheme}>
          <Container>
            <div style={{ color: "#ffffff", width: "125px" }}>
              <Logo size={23} />
            </div>
            {isDownload ? <div>
              <Button
                size="compact"
                onClick={exportScenesToZip}
                disabled={loading}
                kind={KIND.tertiary}
                overrides={{ StartEnhancer: { style: { marginRight: '4px' } } }}
              >
                {loading ? 'Exporting…' : 'Download Files'}
              </Button>
            </div> :

              <>
                {isLoggedIn ? <DesignTitle /> : <div></div>}
                <Block $style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "max-content" }}>
                  <input
                    multiple={false}
                    onChange={handleFileInput}
                    type="file"
                    id="file"
                    ref={inputFileRef}
                    style={{ display: "none" }}
                  />
                  {/*
            <Button
            size="compact"
            onClick={handleInputFileRefClick}
            kind={KIND.tertiary}
            overrides={{
              StartEnhancer: {
                style: {
                  marginRight: "4px",
                },
              },
            }}
          >
            Import
          </Button>

          <Button
            size="compact"
            onClick={makeDownloadTemplate}
            kind={KIND.tertiary}
            overrides={{
              StartEnhancer: {
                style: {
                  marginRight: "4px",
                },
              },
            }}
          >
            Export
          </Button>
             <Button
              size="compact"
              onClick={exportScenesToPdf}
              kind={KIND.tertiary}
              overrides={{
                StartEnhancer: {
                  style: {
                    marginRight: "4px",
                  },
                },
              }}
            >
              PDF Export
            </Button> */}
                  {/* <Button
              size="compact"
              onClick={handleInputFileRefClick}
              kind={KIND.tertiary}
              overrides={{
                StartEnhancer: {
                  style: {
                    marginRight: "4px",
                  },
                },
              }}
            >
              Import
            </Button> */}

                  {!isPremium && (
                    <>
                      <Button
                        size="compact"
                        kind={KIND.tertiary}
                        overrides={{
                          StartEnhancer: {
                            style: {
                              marginRight: "4px",
                            },
                          },
                        }}
                      >
                        R {total.toFixed(2)}
                      </Button>
                      {
                        loading ? <
                          Loading color="#fff" height={50} /> :
                          <Button
                            size="compact"
                            onClick={openModal}
                            kind={KIND.tertiary}
                            overrides={{
                              StartEnhancer: {
                                style: {
                                  marginRight: "4px",
                                },
                              },
                            }}
                          >
                            Add To Basket
                          </Button>
                      }
                    </>
                  )}
                  {isPremium && (
                    <Button
                      size="compact"
                      onClick={finalizePremiumProject}
                      kind={KIND.tertiary}
                      disabled={finalizing}
                      overrides={{
                        StartEnhancer: {
                          style: {
                            marginRight: "4px",
                          },
                        },
                      }}
                    >
                      {finalizing ? "Finalizing..." : "Finalize Premium Order"}
                    </Button>
                  )}
                  <Button
                    size="compact"
                    onClick={() => setDisplayPreview(true)}
                    kind={KIND.tertiary}
                    overrides={{
                      StartEnhancer: {
                        style: {
                          marginRight: "4px",
                        },
                      },
                    }}
                  >
                    Preview
                  </Button>
                  {/* Toggle Removed */}
                  <Button
                    size="compact"
                    onClick={() => exit()}
                    kind={KIND.tertiary}
                    overrides={{
                      StartEnhancer: {
                        style: {
                          marginRight: "4px",
                        },
                      },
                    }}
                  >
                    Exit
                  </Button>
                  {/* <Button
              size="compact"
              onClick={exportScenesToPdf}
              kind={KIND.tertiary}
              overrides={{
                StartEnhancer: {
                  style: {
                    marginRight: "4px",
                  },
                },
              }}
            >
              PDF Export
            </Button> */}
                </Block>
              </>}

          </Container>
        </ThemeProvider>
      }

    </>


  )
}
